import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormattedMessage, useIntl } from 'react-intl';
import { useVisitDetailPhotoCapture } from './visit-detail-photo-capture.hooks';

interface VisitDetailPhotoCaptureProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageData: string, description?: string) => Promise<void>;
}

const VisitDetailPhotoCapture = ({ open, onClose, onCapture }: VisitDetailPhotoCaptureProps) => {
  const intl = useIntl();
  const {
    capturedImage,
    description,
    setDescription,
    isUploading,
    error,
    cameraStream,
    videoRef,
    canvasRef,
    fileInputRef,
    startCamera,
    capturePhoto,
    handleFileSelect,
    handleUpload,
    handleClose,
    handleRetake,
  } = useVisitDetailPhotoCapture(onClose, onCapture);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <FormattedMessage id="hairstyle.photo.title" defaultMessage="Vyfotit účes" />
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          {error && <Alert severity="error">{error}</Alert>}

          {!capturedImage && !cameraStream && (
            <Box display="flex" flexDirection="column" gap={2} alignItems="center" py={4}>
              <Button
                variant="contained"
                startIcon={<CameraAltIcon />}
                onClick={startCamera}
                size="large"
              >
                <FormattedMessage
                  id="hairstyle.photo.start.camera"
                  defaultMessage="Spustit kameru"
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="hairstyle.photo.or" defaultMessage="nebo" />
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => fileInputRef.current?.click()}
                size="large"
              >
                <FormattedMessage id="hairstyle.photo.select.file" defaultMessage="Vybrat soubor" />
              </Button>
            </Box>
          )}
          <Stack spacing={2} alignItems="center" justifyContent="center">
            {!capturedImage && (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  borderRadius: '8px',
                  display: !cameraStream ? 'none' : 'block',
                }}
              />
            )}
            {cameraStream && (
              <Button variant="contained" startIcon={<CameraAltIcon />} onClick={capturePhoto}>
                <FormattedMessage id="hairstyle.photo.capture" defaultMessage="Vyfotit" />
              </Button>
            )}
          </Stack>

          {capturedImage && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box position="relative">
                <img
                  src={capturedImage}
                  alt="Captured"
                  style={{ width: '100%', borderRadius: '8px' }}
                />
                <IconButton
                  onClick={handleRetake}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                label={intl.formatMessage({
                  id: 'hairstyle.photo.description',
                  defaultMessage: 'Popis (volitelné)',
                })}
                value={description}
                onChange={e => setDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
            </Box>
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          <FormattedMessage id="common.cancel" defaultMessage="Zrušit" />
        </Button>
        {capturedImage && (
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : null}
          >
            <FormattedMessage id="hairstyle.photo.upload" defaultMessage="Nahrát" />
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VisitDetailPhotoCapture;
